function computeApiBase() {
  const fromEnv = (process.env.REACT_APP_API_URL || '').trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');

  if (typeof window === 'undefined') return '/api';
  const host = window.location.hostname;
  const protocol = window.location.protocol;

  if (host === 'localhost' || host === '127.0.0.1') {
    return '/api';
  }

  if (host.startsWith('api.')) {
    return `${window.location.origin}/api`;
  }

  const bareHost = host.startsWith('www.') ? host.slice(4) : host;
  return `${protocol}//api.${bareHost}/api`;
}

const API_BASE = computeApiBase();

/** Backend origin for public files (/storage/...) — not the /api JSON prefix. */
export function apiPublicOrigin() {
  const fromEnv = (process.env.REACT_APP_API_URL || '').trim();
  if (fromEnv) {
    try {
      const u = new URL(fromEnv);
      return u.origin;
    } catch {
      /* fall through */
    }
  }
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  if (host === 'localhost' || host === '127.0.0.1') {
    return '';
  }
  if (host.startsWith('api.')) {
    return window.location.origin;
  }
  const bareHost = host.startsWith('www.') ? host.slice(4) : host;
  return `${protocol}//api.${bareHost}`;
}

/**
 * Turn Laravel storage paths/URLs into a URL the browser can load (dev proxy or API host).
 * @param {string|null|undefined} url
 */
export function resolvePublicStorageUrl(url) {
  if (!url) return null;
  let path = String(url).trim();
  if (!path) return null;

  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      path = new URL(path).pathname;
    } catch {
      return url;
    }
  }

  if (!path.startsWith('/storage/')) {
    path = `/storage/${path.replace(/^\/+/, '').replace(/^storage\//, '')}`;
  }

  const origin = apiPublicOrigin();
  return origin ? `${origin}${path}` : path;
}

const API_TOKEN_SESSION_KEY = 'cbonline.apiToken';

export function setApiAccessToken(token) {
  if (!token) return;
  try {
    window.sessionStorage.setItem(API_TOKEN_SESSION_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearApiAccessToken() {
  try {
    window.sessionStorage.removeItem(API_TOKEN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function getApiAccessToken() {
  try {
    return window.sessionStorage.getItem(API_TOKEN_SESSION_KEY) || '';
  } catch {
    return '';
  }
}

/** True when a bearer token is stored (may still validate via /session). */
export function hasApiAccessToken() {
  return Boolean(getApiAccessToken());
}

/**
 * @param {string} path Absolute URL or path beginning with / (relative to API base)
 * @param {RequestInit} [options]
 */
export function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const headers = {
    Accept: 'application/json',
    ...options.headers,
  };
  const bearer = getApiAccessToken();
  if (bearer && !headers.Authorization) {
    headers.Authorization = `Bearer ${bearer}`;
  }
  const body = options.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (!isFormData && body != null && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  return fetch(url, { ...options, headers, credentials: 'include' });
}

/**
 * @param {string} path
 * @param {RequestInit} [options]
 * @returns {Promise<any>}
 */
export async function apiJson(path, options = {}) {
  const res = await apiFetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || res.statusText || 'Request failed');
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}
